import { LOCALES, LOCALE_NAMES, LOCALE_TAGS, type Locale, isLocale } from '../../src/i18n/index';
import type { WidgetSettings } from '../../src/domain/branding';
import {
  T, TN, addDaysISO, api, chooseLocale, errorText, esc, fmtDate, getLocale, labelHhmm, setLocale, todayIn, whenLabel,
  type ApiError,
} from '../shared/client';

/** Wave 3 page state that lives outside the main state object. */
let accessCode = '';
let codeOpen = false;
let eventSent = false;
let alertSentFor = '';
let takenRooms = new Set<string>();

interface Restaurant {
  id: string;
  name: string;
  timezone: string;
  currency: string;
  onlineReservations: boolean;
  suspended?: boolean;
  experiences?: Experience[];
  onlineWaitlistOpen?: boolean;
  hasAccessCodes?: boolean;
  smsEnabled?: boolean;
  marketingEnabled?: boolean;
  menus?: { id: string; titles: Record<string, string>; descriptions: Record<string, string>; sections: { titles: Record<string, string>; items: { names: Record<string, string>; descriptions: Record<string, string>; priceMinor: number | null; labels: string[] }[] }[] }[];
  privateRooms?: { id: string; name: string; description: Record<string, string>; minGuests: number; maxGuests: number }[];
  onlineWaitlist: boolean;
  onlineMinParty: number;
  onlineMaxParty: number;
  diningAreas: { id: string; name: string; environment: string }[];
  policyMessage: string | null;
  policies: { id: string; message: string | null; messages: Partial<Record<Locale, string>> }[];
  defaultLocale: string;
  locales: string[];
  branding: { accentColor: string; logoUrl: string | null; widget: WidgetSettings; profile?: { cuisines: string[]; description: Record<string, string | undefined>; typicalCheck: string | null; gallery: string[] }; legalName?: string | null; contactEmail?: string | null; phone: string | null; address: string | null; website: string | null };
}

interface Slot {
  startsAt: string;
  localTime: string;
  offsetMinutes: number;
  available: boolean;
  reason?: string;
  offerToken?: string;
  diningAreas?: { id: string | null; name: string; environment: string; names?: Record<string, string>; descriptions?: Record<string, string> }[];
  policy?: { kind: string; message: string | null; amountMinor: number; amountType: string; cancelCutoffHours: number } | null;
}

interface Day {
  date: string;
  dayOffset: number;
  slots: Slot[];
  noTimesReasons: string[];
  allowNextAvailable: boolean;
}

type Stage = 'search' | 'details' | 'done' | 'cancelled' | 'waitlist';

interface Experience {
  id: string;
  titles: Partial<Record<string, string>>;
  descriptions: Partial<Record<string, string>>;
  priceMinor: number;
  days: number[];
  dateFrom: string | null;
  dateTo: string | null;
  startTime: string;
  endTime: string;
  minParty: number;
  maxParty: number;
}

const app = document.getElementById('app')!;
const params = new URLSearchParams(location.search);
const embedded = window.parent !== window;
/** Opened from the dashboard's editor: shows unpublished settings sent by that page. */
const previewMode = params.get('preview') === '1';

const S = {
  rest: null as Restaurant | null,
  date: '',
  time: '19:00',
  party: 2,
  days: [] as Day[],
  activeDay: 0,
  searching: false,
  error: '' as string,
  alternatives: [] as { localTime: string; startsAt: string; offerToken: string }[],
  stage: 'search' as Stage,
  expId: '' as string,
  wait: null as null | { id: string; status: string; quotedMinutes: number; waitedMinutes: number; ahead: number; partySize: number },
  waitTimer: 0 as number,
  chosen: null as Slot | null,
  areaId: '' as string,
  lock: null as null | { lockId: string; expiresAt: string; tables: unknown[]; policy: any },
  expiresAt: 0,
  reservation: null as any,
  submitting: false,
  extended: false,
  idem: crypto.randomUUID(),
  pay: null as null | {
    paymentId: string;
    provider: 'mock' | 'stripe';
    publishableKey: string | null;
    clientSecret: string;
    kind: 'card_hold' | 'deposit';
    amountMinor: number;
    currency: string;
  },
};

// Stripe.js is loaded only when the server says Stripe is the provider. Card
// numbers go from this page straight to Stripe; our server never receives them.
let stripe: any = null;
let cardElement: any = null;

function loadStripe(key: string): Promise<any> {
  return new Promise((resolve, reject) => {
    const w = window as any;
    if (w.Stripe) return resolve(w.Stripe(key));
    const el = document.createElement('script');
    el.src = 'https://js.stripe.com/v3/';
    el.onload = () => resolve(w.Stripe(key));
    el.onerror = () => reject(new Error(T('w.card_load_failed')));
    document.head.appendChild(el);
  });
}

const needsPayment = (policy: any) => !!policy && (policy.kind === 'card_hold' || policy.kind === 'deposit');

async function startPayment() {
  S.pay = null;
  stripe = null;
  cardElement = null;
  if (!S.lock || !needsPayment(S.lock.policy)) return;
  try {
    S.pay = await api('POST', `/v1/slot-locks/${S.lock.lockId}/payment`, {});
  } catch (e) {
    const err = e as ApiError;
    S.error = err.code === 'payment_unavailable' ? T('w.call_for_party') : errorText(e);
  }
}

/** Mount the provider's card field into the placeholder the last render produced. */
async function mountCard() {
  const host = document.getElementById('card-element');
  if (!host || !S.pay || S.pay.provider !== 'stripe' || !S.pay.publishableKey) return;
  try {
    stripe = stripe ?? (await loadStripe(S.pay.publishableKey));
    const elements = stripe.elements({ locale: getLocale() });
    cardElement = elements.create('card', { hidePostalCode: false });
    cardElement.mount(host);
  } catch (e) {
    host.textContent = (e as Error).message;
  }
}

let tick: number | undefined;

function notifyParent(type: string, payload: unknown = {}) {
  if (embedded) window.parent.postMessage({ source: 'table-reservations', type, ...((payload as object) ?? {}) }, '*');
}

const FONTS: Record<string, string> = {
  system: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  serif: 'Georgia, "Times New Roman", serif',
  rounded: 'ui-rounded, "Nunito", "Segoe UI", system-ui, sans-serif',
  mono: 'ui-monospace, "SFMono-Regular", Menlo, Consolas, monospace',
};
const WIDTHS: Record<string, string> = { narrow: '440px', normal: '560px', wide: '760px' };
/** Same rule the server enforces on save; applied again here as a second line of defence. */
const FORBIDDEN_CSS = /@import|url\s*\(|image-set|expression\s*\(|behavior\s*:|-moz-binding|javascript:|<\/?style|<script/i;
const isHex = (v: unknown): v is string => typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v);

/** The restaurant's choices for this page, with safe fallbacks. */
const cfg = (): WidgetSettings => S.rest!.branding.widget;

/** The restaurant's own text for a slot (heading, welcome, button, thanks) in the current language. */
function ownText(key: 'title' | 'intro' | 'confirmLabel' | 'thanks'): string {
  const m = cfg()[key] as Partial<Record<Locale, string>> | undefined;
  return (m?.[getLocale()] ?? '').trim();
}

/** Apply the restaurant's look and keep the page title in the current language. */
function applyBranding() {
  const r = S.rest;
  if (!r) return;
  const w = cfg();
  const el = document.documentElement;
  const root = el.style;
  const set = (name: string, value: string | null) => (value ? root.setProperty(name, value) : root.removeProperty(name));

  set('--brand', isHex(r.branding.accentColor) ? r.branding.accentColor : null);
  set('--brand-ink', isHex(r.branding.accentColor) ? readableInk(r.branding.accentColor) : null);
  el.dataset.theme = w.theme;
  set('--bg', isHex(w.background) ? w.background : null);
  set('--panel', isHex(w.panel) ? w.panel : null);
  set('--ink', isHex(w.textColor) ? w.textColor : null);
  // Muted text and lines follow the chosen text and background colours.
  set('--muted', isHex(w.textColor) ? `color-mix(in srgb, ${w.textColor} 62%, ${isHex(w.background) ? w.background : 'transparent'})` : null);
  set('--line', isHex(w.textColor) ? `color-mix(in srgb, ${w.textColor} 22%, transparent)` : null);
  set('--radius', `${w.radius}px`);
  set('--font', FONTS[w.font] ?? FONTS.system);
  set('--page-width', WIDTHS[w.width] ?? WIDTHS.normal);
  el.classList.toggle('btn-outline', w.buttonStyle === 'outline');
  el.classList.toggle('head-center', w.headerAlign === 'center');

  let style = document.getElementById('custom-css') as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement('style');
    style.id = 'custom-css';
    document.head.appendChild(style);
  }
  // textContent, never innerHTML: the text cannot close the element or add markup.
  style.textContent = w.customCss && !FORBIDDEN_CSS.test(w.customCss) ? w.customCss : '';
  document.title = T('w.document_title', { restaurant: r.name });
}

/** Black or white text, whichever reads better on the accent colour. */
function readableInk(hex: string): string {
  const n = parseInt(hex.slice(1), 16);
  const lum = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return lum > 0.6 ? '#111111' : '#ffffff';
}

/** Used only if an older server sent no widget settings. */
const DEFAULT_WIDGET_VIEW = {
  theme: 'light', background: null, panel: null, textColor: null, font: 'system', radius: 12, width: 'normal',
  buttonStyle: 'solid', headerAlign: 'left', showLogo: true, showName: true, title: {}, intro: {}, confirmLabel: {},
  thanks: {}, termsRequired: false, showMenus: false, termsText: {}, defaultParty: 2, daysShown: 4, timeFrom: '08:00', timeTo: '22:00', customCss: '',
  fields: { phone: 'optional', occasion: true, request: true, marketing: true, sms: true, area: true },
};

const clampParty = (n: number) => {
  const r = S.rest!;
  return Math.min(r.onlineMaxParty, Math.max(r.onlineMinParty, Number.isFinite(n) ? n : 2));
};

async function init() {
  // ?rid=, else the restaurant this domain belongs to, else the only restaurant on this server.
  const rid =
    params.get('rid') ??
    (await api<{ restaurantId: string | null }>('GET', '/v1/site').catch(() => ({ restaurantId: null }))).restaurantId ??
    (await api<{ restaurants: { id: string }[] }>('GET', '/v1/restaurants')).restaurants[0]?.id;
  if (!rid) {
    app.innerHTML = `<p class="notice error">${esc(T('w.no_restaurant'))}</p>`;
    return;
  }
  S.rest = await api<Restaurant>('GET', `/v1/restaurants/${rid}`);
  S.rest.branding.widget = { ...DEFAULT_WIDGET_VIEW, ...(S.rest.branding.widget ?? {}) } as WidgetSettings;
  setLocale(chooseLocale(S.rest.locales, S.rest.defaultLocale), false);
  applyBranding();
  S.date = params.get('date') ?? addDaysISO(todayIn(S.rest.timezone), 1);
  S.party = clampParty(Number(params.get('party') ?? S.rest.branding.widget.defaultParty ?? 2));
  S.time = params.get('time') ?? '19:00';
  {
    const w = S.rest.branding.widget;
    if (S.time < w.timeFrom) S.time = w.timeFrom;
    if (S.time > w.timeTo) S.time = w.timeTo;
  }

  S.expId = params.get('exp') ?? '';
  const wl = params.get('wl');
  if (wl && /^[0-9a-f-]{36}$/i.test(wl)) {
    await refreshWait(wl);
    if (S.wait) S.stage = 'waitlist';
  } else if (params.get('mode') === 'waitlist' && S.rest.onlineWaitlistOpen) S.stage = 'waitlist';

  // Returning diner: ?code=XXXX shows the booking.
  const code = params.get('code');
  if (code) {
    try {
      S.reservation = await api('GET', `/v1/reservations/${code}`);
      S.stage = S.reservation.status === 'cancelled' ? 'cancelled' : 'done';
    } catch {
      S.error = T('w.code_not_found');
    }
  }
  render();
  if (previewMode) window.parent.postMessage({ source: 'table-reservations', type: 'preview-ready' }, location.origin);
  if (S.stage === 'search' && S.rest.onlineReservations) void search();
  if (S.stage === 'waitlist' && S.wait) startWaitPolling();
}

async function refreshWait(id: string) {
  try {
    S.wait = { id, ...(await api<any>('GET', `/v1/waitlist/${id}/status`)) };
  } catch {
    S.wait = null;
  }
}

function startWaitPolling() {
  window.clearInterval(S.waitTimer);
  S.waitTimer = window.setInterval(async () => {
    if (S.stage !== 'waitlist' || !S.wait) return;
    await refreshWait(S.wait.id);
    render();
  }, 20000);
}

async function search() {
  if (!S.rest) return;
  S.searching = true;
  S.error = '';
  S.chosen = null;
  render();
  try {
    const res = await api<{ days: Day[] }>('POST', '/v1/availability/search', {
      restaurantId: S.rest.id,
      date: S.date,
      time: S.time,
      partySize: S.party,
      windowMinutes: 180,
      daysForward: Math.max(0, cfg().daysShown - 1),
      experienceId: S.expId || undefined,
      accessCode: accessCode || undefined,
      source: 'website',
    });
    S.days = res.days;
    S.activeDay = 0;
  } catch (e) {
    S.error = errorText(e);
  } finally {
    S.searching = false;
    render();
  }
}

async function choose(slot: Slot) {
  if (!slot.offerToken) return;
  S.chosen = slot;
  S.error = '';
  S.alternatives = [];
  S.areaId = '';
  await takeHold();
}

async function takeHold() {
  if (!S.chosen?.offerToken) return;
  try {
    const lock = await api<any>('POST', '/v1/slot-locks', {
      offerToken: S.chosen.offerToken,
      diningAreaId: S.areaId || undefined,
      idempotencyKey: S.idem,
    });
    S.lock = lock;
    S.expiresAt = new Date(lock.expiresAt).getTime();
    await startPayment();
    S.stage = 'details';
    S.extended = false;
    startTimer();
    notifyParent('hold', { expiresAt: lock.expiresAt });
  } catch (e) {
    const err = e as ApiError;
    S.chosen = null;
    if (err.code === 'slot_unavailable') {
      S.error = T('w.taken');
      S.alternatives = err.details?.alternatives ?? [];
    } else if (err.code === 'offer_expired') {
      S.error = T('w.offer_stale');
      await search();
      return;
    } else {
      S.error = errorText(e);
    }
    S.idem = crypto.randomUUID();
  }
  render();
}

function startTimer() {
  window.clearInterval(tick);
  tick = window.setInterval(() => {
    const el = document.getElementById('timer');
    const left = Math.max(0, Math.floor((S.expiresAt - Date.now()) / 1000));
    if (el) {
      el.textContent = `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`;
      el.classList.toggle('low', left <= 60);
    }
    if (left === 0 && S.stage === 'details') {
      window.clearInterval(tick);
      S.stage = 'search';
      S.lock = null;
      S.chosen = null;
      S.error = T('w.hold_expired');
      S.idem = crypto.randomUUID();
      render();
      void search();
    }
  }, 500);
}

async function extend() {
  if (!S.lock || S.extended) return;
  try {
    const l = await api<any>('POST', `/v1/slot-locks/${S.lock.lockId}/extend`, {});
    S.expiresAt = new Date(l.expiresAt).getTime();
    S.extended = true;
    render();
  } catch (e) {
    S.error = errorText(e);
    render();
  }
}

async function release() {
  window.clearInterval(tick);
  if (S.lock) {
    try {
      await api('DELETE', `/v1/slot-locks/${S.lock.lockId}`);
    } catch {
      /* the hold expires on its own */
    }
  }
  S.lock = null;
  S.chosen = null;
  S.stage = 'search';
  S.idem = crypto.randomUUID();
  render();
}

async function confirm(form: HTMLFormElement) {
  if (!S.lock || S.submitting) return;
  const fd = new FormData(form);
  S.submitting = true;
  S.error = '';
  render();
  try {
    if (S.pay) await completeCard(fd);
    const occasion = String(fd.get('occasion') ?? '');
    const r = await api('POST', '/v1/reservations', {
      lockId: S.lock.lockId,
      idempotencyKey: S.idem,
      guest: {
        firstName: String(fd.get('firstName') ?? '').trim(),
        lastName: String(fd.get('lastName') ?? '').trim(),
        email: String(fd.get('email') ?? '').trim() || null,
        phone: String(fd.get('phone') ?? '').trim() || null,
        marketingOptIn: fd.get('marketing') === 'on',
        smsOptIn: fd.get('sms') === 'on',
      },
      occasion: occasion || null,
      specialRequest: String(fd.get('request') ?? '').trim() || null,
      paymentToken: S.pay ? S.pay.paymentId : null,
      experienceId: S.expId || null,
      accessCode: accessCode || null,
      locale: getLocale(),
      acceptedTerms: fd.get('terms') === 'on',
    });
    window.clearInterval(tick);
    S.reservation = r;
    S.stage = 'done';
    notifyParent('confirmed', { code: (r as any).code });
  } catch (e) {
    const err = e as ApiError;
    if (err.code === 'lock_expired') {
      S.stage = 'search';
      S.lock = null;
      S.error = T('w.hold_expired_confirm');
      S.idem = crypto.randomUUID();
      void search();
    } else {
      S.error = errorText(e);
    }
  } finally {
    S.submitting = false;
    render();
  }
}

/** Run the card step with the provider. Throws a user-facing Error on failure. */
async function completeCard(fd: FormData) {
  const pay = S.pay!;
  if (pay.provider === 'mock') {
    await api('POST', '/v1/dev/mock-payments/complete', { clientSecret: pay.clientSecret });
    return;
  }
  if (!stripe || !cardElement) throw new Error(T('w.card_not_ready'));
  const billing = {
    name: `${String(fd.get('firstName') ?? '')} ${String(fd.get('lastName') ?? '')}`.trim(),
    email: String(fd.get('email') ?? '') || undefined,
  };
  const args = { payment_method: { card: cardElement, billing_details: billing } };
  const result =
    pay.kind === 'card_hold'
      ? await stripe.confirmCardSetup(pay.clientSecret, args)
      : await stripe.confirmCardPayment(pay.clientSecret, args);
  if (result.error) throw new Error(result.error.message ?? T('w.card_failed'));
}

async function applyCode(code: string) {
  if (!S.rest) return;
  try {
    const before = (S.rest.experiences ?? []).length;
    S.rest = await api<Restaurant>('GET', `/v1/restaurants/${S.rest.id}?code=${encodeURIComponent(code)}`);
    if ((S.rest.experiences ?? []).length <= before) {
      accessCode = '';
      S.error = T('w.code.bad');
    } else {
      accessCode = code;
      S.error = '';
      codeOpen = false;
    }
  } catch (e) {
    S.error = errorText(e);
  }
  render();
}

async function sendEvent(form: HTMLFormElement) {
  if (!S.rest) return;
  const fd = new FormData(form);
  try {
    await api('POST', `/v1/restaurants/${S.rest.id}/private-requests`, {
      roomId: String(fd.get('room') ?? '') || null,
      name: String(fd.get('name') ?? '').trim(),
      company: String(fd.get('company') ?? '').trim() || null,
      email: String(fd.get('email') ?? '').trim(),
      phone: String(fd.get('phone') ?? '').trim() || null,
      guests: Number(fd.get('guests')),
      date: String(fd.get('date')),
      startTime: String(fd.get('from')),
      endTime: String(fd.get('to')),
      notes: String(fd.get('notes') ?? '').trim() || null,
      locale: getLocale(),
    });
    eventSent = true;
    S.error = '';
  } catch (e) {
    S.error = errorText(e);
  }
  render();
}

async function sendAlert(form: HTMLFormElement) {
  if (!S.rest) return;
  const fd = new FormData(form);
  const date = form.dataset.date ?? S.date;
  try {
    await api('POST', `/v1/restaurants/${S.rest.id}/alerts`, {
      name: String(fd.get('name') ?? '').trim(),
      email: String(fd.get('email') ?? '').trim(),
      partySize: S.party,
      date,
      fromTime: String(fd.get('from')),
      toTime: String(fd.get('to')),
      locale: getLocale(),
    });
    alertSentFor = date;
    S.error = '';
  } catch (e) {
    S.error = errorText(e);
  }
  render();
}

async function joinWaitlist(form: HTMLFormElement) {
  if (!S.rest || S.submitting) return;
  const fd = new FormData(form);
  S.submitting = true;
  S.error = '';
  render();
  try {
    const r = await api<{ id: string }>('POST', `/v1/restaurants/${S.rest.id}/waitlist/join`, {
      name: String(fd.get('name') ?? '').trim(),
      partySize: Number(fd.get('party')),
      phone: String(fd.get('phone') ?? '').trim(),
      smsOptIn: fd.get('sms') === 'on',
      locale: getLocale(),
    });
    history.replaceState(null, '', `${location.pathname}?rid=${S.rest.id}&wl=${r.id}`);
    await refreshWait(r.id);
    startWaitPolling();
    notifyParent('waitlisted', { id: r.id });
  } catch (e) {
    S.error = errorText(e);
  } finally {
    S.submitting = false;
    render();
  }
}

async function cancelBooking() {
  if (!S.reservation) return;
  if (!window.confirm(T('w.cancel_confirm'))) return;
  try {
    const r = await api<any>('POST', `/v1/reservations/${S.reservation.code}/cancel`, {});
    S.reservation = r.reservation;
    S.stage = 'cancelled';
    S.error = r.feeDue ? T('w.cancel_fee') : '';
  } catch (e) {
    S.error = errorText(e);
  }
  render();
}

// ------------------------------------------------------------------ views

const fmtMoney = (minor: number, cur: string) =>
  new Intl.NumberFormat(LOCALE_TAGS[getLocale()], { style: 'currency', currency: cur }).format(minor / 100);

/** The restaurant's own wording for a policy, in the current language. */
function policyText(policy: { id?: string; message?: string | null } | null): string {
  const r = S.rest!;
  const loc = getLocale();
  const entry = r.policies.find((p) => p.id === policy?.id) ?? r.policies.find((p) => p.message || Object.keys(p.messages).length);
  return entry?.messages[loc] || entry?.message || policy?.message || '';
}

const reason = (code: string | undefined) => (code ? T(`reason.${code}`) : '');
const partyLabel = (n: number) => TN('common.guests', n);

function brandHeader(): string {
  const r = S.rest!;
  const w = cfg();
  const logo = r.branding.logoUrl && w.showLogo
    ? `<img class="logo" src="${esc(r.branding.logoUrl)}" alt="${w.showName ? '' : esc(r.name)}" referrerpolicy="no-referrer">`
    : '';
  const heading = ownText('title') || r.name;
  const langs = r.locales.filter(isLocale);
  const switcher =
    langs.length > 1
      ? `<span class="lang" role="group" aria-label="${esc(T('lang.label'))}">${langs
          .map(
            (l) =>
              `<button type="button" class="link${l === getLocale() ? ' on' : ''}" data-lang="${l}" aria-pressed="${l === getLocale()}" lang="${l}">${esc(l.toUpperCase())}</button>`,
          )
          .join('')}</span>`
      : '';
  const h1 = w.showName || ownText('title') ? `<h1>${esc(heading)}</h1>` : `<h1 class="sr">${esc(r.name)}</h1>`;
  return `<div class="brand">${logo}${h1}${switcher}</div>`;
}

const pick = (m: Record<string, string> | undefined) =>
  (m && (m[getLocale()] || m.en || (Object.values(m).find(Boolean) as string | undefined))) || '';

function menusBlock(menus: NonNullable<Restaurant['menus']>): string {
  return `<details class="menus"><summary>${esc(T('w.menu.show'))}</summary>${menus
    .map(
      (m) => `<section><h3>${esc(pick(m.titles))}</h3>${pick(m.descriptions) ? `<p class="small muted">${esc(pick(m.descriptions))}</p>` : ''}
      ${m.sections
        .map(
          (s) => `${pick(s.titles) ? `<h4>${esc(pick(s.titles))}</h4>` : ''}<ul class="menu-items">${s.items
            .map(
              (i) => `<li><span class="mi-name">${esc(pick(i.names))}${i.labels.map((l) => ` <span class="chip">${esc(l)}</span>`).join('')}</span>
              ${i.priceMinor != null ? `<span class="mi-price">${esc(fmtMoney(i.priceMinor, S.rest!.currency))}</span>` : ''}
              ${pick(i.descriptions) ? `<span class="small muted mi-desc">${esc(pick(i.descriptions))}</span>` : ''}</li>`,
            )
            .join('')}</ul>`,
        )
        .join('')}</section>`,
    )
    .join('')}</details>`;
}

function profileBlock(): string {
  const p = S.rest!.branding.profile;
  if (!p) return '';
  const desc = p.description[getLocale()] || p.description.en || (Object.values(p.description).find(Boolean) as string | undefined) || '';
  const facts = [...p.cuisines, ...(p.typicalCheck ? [p.typicalCheck] : [])];
  const menus = cfg().showMenus ? (S.rest!.menus ?? []) : [];
  if (!desc && !facts.length && !p.gallery.length && !menus.length) return '';
  return `<section class="profile" aria-label="${esc(T('w.about'))}">
    ${facts.length ? `<p class="small muted facts">${facts.map(esc).join(' · ')}</p>` : ''}
    ${desc ? `<p class="desc">${esc(desc)}</p>` : ''}
    ${menus.length ? menusBlock(menus) : ''}
    ${p.gallery.length ? `<div class="gallery">${p.gallery.map((g) => `<img src="${esc(g)}" alt="" loading="lazy" referrerpolicy="no-referrer">`).join('')}</div>` : ''}
  </section>`;
}

function contactFooter(): string {
  const b = S.rest!.branding;
  const lines: string[] = [];
  if (b.legalName) lines.push(esc(b.legalName));
  if (b.contactEmail) lines.push(`<a href="mailto:${esc(b.contactEmail)}">${esc(b.contactEmail)}</a>`);
  if (b.phone) lines.push(`<a href="tel:${esc(b.phone.replace(/[^+\d]/g, ''))}">${esc(b.phone)}</a>`);
  if (b.address) lines.push(esc(b.address));
  if (b.website) lines.push(`<a href="${esc(b.website)}" target="_blank" rel="noopener noreferrer">${esc(b.website.replace(/^https:\/\//, ''))}</a>`);
  return lines.length ? `<footer class="small muted contact" aria-label="${esc(T('w.contact'))}">${lines.join(' · ')}</footer>` : '';
}

function experienceName(x: Experience): string {
  return x.titles[getLocale()] || x.titles.en || (Object.values(x.titles)[0] as string) || '';
}

function codeControl(): string {
  if (!S.rest!.hasAccessCodes && !accessCode) return '';
  return codeOpen
    ? `<form id="codeForm" class="row" style="display:flex;gap:8px;align-items:end;margin:8px 0"><label style="flex:1">${esc(T('w.code.label'))}<input name="code" maxlength="40" autocomplete="off" value="${esc(accessCode)}"></label><button type="submit">${esc(T('w.code.apply'))}</button></form>`
    : `<p class="small"><button class="link" type="button" data-act="code-open">${esc(T('w.code.link'))}</button></p>`;
}

function eventsBlock(): string {
  const rooms = S.rest!.privateRooms ?? [];
  if (!rooms.length) return '';
  if (eventSent) return `<div class="notice" style="margin-top:20px">${esc(T('w.event.thanks'))}</div>`;
  const today = new Date().toISOString().slice(0, 10);
  return `<details class="events" style="margin-top:20px"><summary>${esc(T('w.event.title'))}</summary>
    <p class="small muted">${esc(T('w.event.intro'))}</p>
    <form id="eventForm" class="stack">
      <label>${esc(T('w.event.room'))}<select name="room"><option value="">${esc(T('w.event.any'))}</option>${rooms
        .map((r) => `<option value="${r.id}" ${takenRooms.has(r.id) ? 'disabled' : ''}>${esc(r.name)} (${r.minGuests}–${r.maxGuests})${takenRooms.has(r.id) ? ' · ' + esc(T('w.event.taken')) : ''}</option>`)
        .join('')}</select></label>
      <div class="row2"><label>${esc(T('w.event.guests'))}<input name="guests" type="number" min="1" max="1000" required value="10"></label>
        <label>${esc(T('w.event.date'))}<input name="date" type="date" min="${today}" required></label></div>
      <div class="row2"><label>${esc(T('w.event.from'))}<input name="from" type="time" required value="18:00"></label>
        <label>${esc(T('w.event.to'))}<input name="to" type="time" required value="23:00"></label></div>
      <label>${esc(T('w.event.name'))}<input name="name" required maxlength="100" autocomplete="name"></label>
      <label>${esc(T('w.event.company'))}<input name="company" maxlength="100" autocomplete="organization"></label>
      <label>${esc(T('w.event.email'))}<input name="email" type="email" required maxlength="200" autocomplete="email"></label>
      <label>${esc(T('w.event.phone'))}<input name="phone" type="tel" maxlength="40" autocomplete="tel"></label>
      <label>${esc(T('w.event.notes'))}<textarea name="notes" rows="3" maxlength="1000"></textarea></label>
      <button class="primary" type="submit">${esc(T('w.event.send'))}</button>
    </form></details>`;
}

function alertForm(date: string): string {
  if (alertSentFor === date) return `<div class="notice">${esc(T('w.alert.thanks'))}</div>`;
  return `<details class="alert" style="margin-top:12px"><summary>${esc(T('w.alert.title'))}</summary>
    <p class="small muted">${esc(T('w.alert.intro'))}</p>
    <form id="alertForm" class="stack" data-date="${esc(date)}">
      <div class="row2"><label>${esc(T('w.alert.from'))}<input name="from" type="time" value="${esc(cfg().timeFrom)}" required></label>
        <label>${esc(T('w.alert.to'))}<input name="to" type="time" value="${esc(cfg().timeTo)}" required></label></div>
      <label>${esc(T('w.event.name'))}<input name="name" required maxlength="80" autocomplete="name"></label>
      <label>${esc(T('w.event.email'))}<input name="email" type="email" required maxlength="200" autocomplete="email"></label>
      <button class="primary" type="submit">${esc(T('w.alert.send'))}</button>
    </form></details>`;
}

function experienceCards(): string {
  const xs = S.rest!.experiences ?? [];
  if (!xs.length) return codeControl();
  const money = (m: number) => fmtMoney(m, S.rest!.currency);
  const card = (id: string, title: string, desc: string, meta: string) =>
    `<button type="button" class="exp ${S.expId === id ? 'on' : ''}" data-exp="${id}" aria-pressed="${S.expId === id}">
      <strong>${esc(title)}</strong>${desc ? `<span class="small">${esc(desc)}</span>` : ''}<span class="small muted">${esc(meta)}</span></button>`;
  return `<div class="exps" role="group" aria-label="${esc(T('w.exp.title'))}"><h2>${esc(T('w.exp.title'))}</h2>
    ${card('', T('w.exp.any'), '', '')}
    ${xs.map((x) => card(x.id, experienceName(x), x.descriptions[getLocale()] || x.descriptions.en || '',
      `${(x as any).restricted ? T('w.member') + ' · ' : ''}${x.priceMinor ? T('w.exp.price', { price: money(x.priceMinor) }) + ' · ' : ''}${labelHhmm(x.startTime)}–${labelHhmm(x.endTime)} · ${x.minParty}–${x.maxParty}`)).join('')}</div>${codeControl()}`;
}

function waitlistView(): string {
  const r = S.rest!;
  const w = S.wait;
  if (w) {
    const done = w.status === 'seated' || w.status === 'removed' || w.status === 'cancelled';
    return `${brandHeader()}
      <h2>${esc(T('w.wl.title'))}</h2>
      <div class="card">
        <p class="${w.status === 'notified' ? 'notice ok' : ''}" style="margin:0 0 8px"><strong>${esc(
          w.status === 'notified' ? T('w.wl.ready') : done ? T('w.wl.done') : T('w.wl.waiting'),
        )}</strong></p>
        ${done ? '' : `<dl class="summary"><dt>${esc(T('w.wl.quote'))}</dt><dd>${esc(T('w.wl.minutes', { n: w.quotedMinutes }))}</dd>
          <dt>${esc(T('w.wl.waited'))}</dt><dd>${esc(T('w.wl.minutes', { n: w.waitedMinutes }))}</dd>
          ${w.status === 'waiting' ? `<dt>${esc(T('w.wl.ahead'))}</dt><dd>${w.ahead}</dd>` : ''}</dl>
          <p class="small muted">${esc(T('w.wl.keep_open'))}</p>`}
      </div>${contactFooter()}`;
  }
  return `${brandHeader()}
    <h2>${esc(T('w.wl.join'))}</h2>
    ${S.error ? `<div class="notice error" role="alert">${esc(S.error)}</div>` : ''}
    ${
      !r.onlineWaitlistOpen
        ? `<div class="notice">${esc(T('w.wl.closed'))}</div>`
        : `<form id="waitForm" class="card">
      <div class="row">
        <div class="field"><label for="wl-name">${esc(T('w.wl.name'))}</label><input id="wl-name" name="name" required maxlength="80" autocomplete="name"></div>
        <div class="field"><label for="wl-party">${esc(T('w.party_size'))}</label><input id="wl-party" name="party" type="number" min="1" max="40" value="${S.party}" required></div>
      </div>
      <div class="field" style="margin-top:10px"><label for="wl-phone">${esc(T('w.phone'))} *</label><input id="wl-phone" name="phone" type="tel" required autocomplete="tel"></div>
      ${S.rest!.smsEnabled ? `<label class="check"><input type="checkbox" name="sms"> <span>${esc(T('w.wl.sms'))}</span></label>` : ''}
      <p><button class="primary" type="submit" ${S.submitting ? 'disabled' : ''}>${esc(T('w.wl.submit'))}</button></p></form>`
    }${contactFooter()}`;
}

function searchView(): string {
  const r = S.rest!;
  const xp = (r.experiences ?? []).find((x) => x.id === S.expId);
  const lo = xp ? Math.max(r.onlineMinParty, xp.minParty) : r.onlineMinParty;
  const hi = xp ? Math.min(r.onlineMaxParty, xp.maxParty) : r.onlineMaxParty;
  const parties = Array.from({ length: Math.max(1, hi - lo + 1) }, (_, i) => lo + i);
  const times: string[] = [];
  const toMin = (v: string) => Number(v.slice(0, 2)) * 60 + Number(v.slice(3, 5));
  const from = toMin(cfg().timeFrom);
  const to = Math.max(from, toMin(cfg().timeTo));
  for (let m = from; m <= to; m += 30) times.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
  const day = S.days[S.activeDay];
  return `
  ${brandHeader()}
  ${profileBlock()}
  <p class="muted">${esc(ownText('intro') || T('w.reserve'))}</p>
  ${S.error ? `<div class="notice error" role="alert">${esc(S.error)}</div>` : ''}
  ${experienceCards()}
  ${
    r.suspended
      ? `<div class="notice">${esc(T('w.online_off'))}</div>`
      : !r.onlineReservations
      ? `<div class="notice">${esc(T(r.onlineWaitlist ? 'w.online_off_waitlist' : 'w.online_off'))}</div>`
      : `
  <form id="searchForm" class="card" aria-label="${esc(T('w.find_aria'))}">
    <div class="row">
      <div class="field"><label for="date">${esc(T('w.date'))}</label><input id="date" type="date" name="date" value="${esc(S.date)}" min="${esc(todayIn(r.timezone))}" required></div>
      <div class="field"><label for="time">${esc(T('w.time'))}</label>
        <select id="time" name="time">${times.map((t) => `<option value="${t}" ${t === S.time ? 'selected' : ''}>${labelHhmm(t)}</option>`).join('')}</select></div>
      <div class="field"><label for="party">${esc(T('w.party_size'))}</label>
        <select id="party" name="party">${parties.map((p) => `<option value="${p}" ${p === S.party ? 'selected' : ''}>${esc(partyLabel(p))}</option>`).join('')}</select></div>
    </div>
    <p style="margin:12px 0 0"><button class="primary" type="submit" ${S.searching ? 'disabled' : ''}>${esc(S.searching ? T('w.searching') : T('w.find'))}</button></p>
  </form>`
  }
  ${S.alternatives.length ? `<div class="card" style="margin-top:12px"><strong>${esc(T('w.nearby'))}</strong><div class="slots" style="margin-top:8px">${S.alternatives
    .map((a, i) => `<button class="slot" data-alt="${i}">${labelHhmm(a.localTime)}</button>`).join('')}</div></div>` : ''}
  ${day ? daysView(day) : ''}
  ${r.onlineWaitlistOpen && S.days.length && S.days.every((d) => d.slots.every((x) => !x.available)) ? `<div class="card" style="margin-top:12px"><p style="margin:0 0 8px">${esc(T('w.wl.offer'))}</p><button data-act="to-waitlist">${esc(T('w.wl.join'))}</button></div>` : ''}
  ${contactFooter()}`;
}

function daysView(day: Day): string {
  return `
  <h2>${esc(T('w.available_times'))}</h2>
  <div class="days" role="tablist" aria-label="${esc(T('w.day_aria'))}">
    ${S.days
      .map((d, i) => {
        const n = d.slots.filter((s) => s.available).length;
        return `<button class="day" role="tab" data-day="${i}" aria-selected="${i === S.activeDay}">${esc(fmtDate(d.date))}<br><span class="small muted">${esc(n ? TN('w.times', n) : T('w.none'))}</span></button>`;
      })
      .join('')}
  </div>
  ${
    day.slots.every((s) => !s.available)
      ? `<div class="notice">${esc(T('w.no_tables', { party: S.party, date: fmtDate(day.date) }))}${day.noTimesReasons.length ? ` (${esc(reason(day.noTimesReasons[0]))})` : ''}.${day.allowNextAvailable ? ` ${esc(T('w.try_other_day'))}` : ''}</div>${alertForm(day.date)}`
      : `<div class="slots">${day.slots
          .map((s, i) =>
            s.available
              ? `<button class="slot" data-slot="${i}" aria-pressed="${S.chosen?.startsAt === s.startsAt}">${labelHhmm(s.localTime)}${s.policy && s.policy.kind !== 'none' ? `<span class="why">${esc(T('w.card_required'))}</span>` : ''}</button>`
              : `<button class="slot off" disabled aria-label="${labelHhmm(s.localTime)} ${esc(T('w.unavailable'))}">${labelHhmm(s.localTime)}<span class="why">${esc(reason(s.reason))}</span></button>`,
          )
          .join('')}</div>`
  }
  ${policyText(null) ? `<p class="small muted" style="margin-top:12px">${esc(policyText(null))}</p>` : ''}
  ${eventsBlock()}`;
}

/** The restaurant's description of the chosen seating area, in the guest's language. */
function areaNote(areas: NonNullable<Slot['diningAreas']>, selected: string): string {
  const a = areas.find((x) => x.id === selected) ?? areas[0];
  const text = a?.descriptions?.[getLocale()]?.trim();
  return text ? `<span class="small muted">${esc(text)}</span>` : '';
}

function paymentBlock(): string {
  const p = S.pay;
  if (!p) return '';
  const amount = esc(fmtMoney(p.amountMinor, p.currency));
  // The dictionary text is ours and contains <strong>; only the amount is inserted.
  const what = T(p.kind === 'deposit' ? 'w.pay.deposit' : 'w.pay.card', { amount });
  return `<fieldset class="pay"><legend>${esc(T('w.pay.legend'))}</legend>
    <p class="small" style="margin:0 0 8px">${what}</p>
    ${
      p.provider === 'mock'
        ? `<p class="notice small" style="margin:0">${esc(T('w.pay.test'))}</p>`
        : `<div id="card-element" class="card-element" aria-label="${esc(T('w.pay.card_aria'))}"></div>`
    }
  </fieldset>`;
}

const OCCASIONS = ['birthday', 'anniversary', 'date_night', 'business', 'celebration'];

function detailsView(): string {
  const slot = S.chosen!;
  const policy = S.lock?.policy as { id?: string; kind: string; message?: string; amountMinor: number; amountType: string; cancelCutoffHours: number } | null;
  const left = Math.max(0, Math.floor((S.expiresAt - Date.now()) / 1000));
  const areas = (slot.diningAreas ?? []).filter((a) => a.id);
  const timer = `<span id="timer" class="timer">${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}</span>`;
  const heldFor = T('w.held_for', { timer: '\u0000' }).split('\u0000').map(esc).join(timer);
  return `
  ${brandHeader()}
  <div class="card">
    <div style="display:flex;justify-content:space-between;gap:8px;align-items:center">
      <strong>${esc(whenLabel(slot.startsAt, S.rest!.timezone))}</strong>
      <span>${heldFor}</span>
    </div>
    <p class="muted" style="margin:4px 0 0">${esc(T('w.table_for', { n: S.party }))}</p>
    <p style="margin:8px 0 0">
      <button class="link" type="button" data-act="back">${esc(T('w.change_time'))}</button>
      ${S.extended ? '' : `<button class="link" type="button" data-act="extend">${esc(T('w.more_time'))}</button>`}
    </p>
  </div>
  ${S.error ? `<div class="notice error" role="alert">${esc(S.error)}</div>` : ''}
  ${
    policy && policy.kind !== 'none'
      ? `<div class="notice"><strong>${esc(T('w.policy.title'))}</strong><br>${esc(policyText(policy))}<br>
          <span class="small muted">${esc(
            T('w.policy.detail', {
              kind: T(policy.kind === 'deposit' ? 'w.policy.deposit' : 'w.policy.card'),
              amount: fmtMoney(policy.amountMinor, S.rest!.currency),
              per: T(policy.amountType === 'per_guest' ? 'w.policy.per_guest' : 'w.policy.flat'),
              hours: policy.cancelCutoffHours,
            }),
          )}</span></div>`
      : ''
  }
  <form id="detailsForm">
    ${
      areas.length > 1 && cfg().fields.area
        ? `<div class="field" style="margin-bottom:10px"><label for="area">${esc(T('w.seating'))}</label><select id="area" name="area">${areas
            .map((a) => {
              const shown = a.names?.[getLocale()]?.trim() || a.name;
              return `<option value="${a.id}" ${a.id === S.areaId ? 'selected' : ''}>${esc(shown)}</option>`;
            })
            .join('')}</select>${areaNote(areas, S.areaId)}</div>`
        : ''
    }
    <div class="row">
      <div class="field"><label for="firstName">${esc(T('w.first_name'))}</label><input id="firstName" name="firstName" autocomplete="given-name" required></div>
      <div class="field"><label for="lastName">${esc(T('w.last_name'))}</label><input id="lastName" name="lastName" autocomplete="family-name" required></div>
    </div>
    <div class="row" style="margin-top:10px">
      <div class="field"><label for="email">${esc(T('w.email'))}</label><input id="email" name="email" type="email" autocomplete="email" required></div>
      ${
        cfg().fields.phone === 'hidden'
          ? ''
          : `<div class="field"><label for="phone">${esc(T('w.phone'))}${cfg().fields.phone === 'required' ? ' *' : ''}</label><input id="phone" name="phone" type="tel" autocomplete="tel" ${cfg().fields.phone === 'required' ? 'required' : ''}></div>`
      }
    </div>
    ${
      cfg().fields.occasion
        ? `<div class="row" style="margin-top:10px">
      <div class="field"><label for="occasion">${esc(T('w.occasion'))}</label>
        <select id="occasion" name="occasion"><option value="">${esc(T('occasion.none'))}</option>${OCCASIONS.map((o) => `<option value="${o}">${esc(T(`occasion.${o}`))}</option>`).join('')}</select></div>
    </div>`
        : ''
    }
    ${
      cfg().fields.request
        ? `<div class="field" style="margin-top:10px"><label for="request">${esc(T('w.request'))}</label><textarea id="request" name="request" rows="2" maxlength="500"></textarea></div>`
        : ''
    }
    ${S.rest!.smsEnabled && cfg().fields.sms && cfg().fields.phone !== 'hidden' ? `<label class="check"><input type="checkbox" name="sms"> <span>${esc(T('w.sms'))}</span></label>` : ''}
    ${S.rest!.marketingEnabled && cfg().fields.marketing ? `<label class="check"><input type="checkbox" name="marketing"> <span>${esc(T('w.marketing'))}</span></label>` : ''}
    ${cfg().termsRequired ? `<label class="check"><input type="checkbox" name="terms" required> <span>${esc(((cfg().termsText as Partial<Record<Locale, string>>)?.[getLocale()] ?? '').trim() || T('w.terms'))}</span></label>` : ''}
    ${paymentBlock()}
    <p><button class="primary" type="submit" ${S.submitting || (needsPayment(policy) && !S.pay) ? 'disabled' : ''}>${esc(
      S.submitting
        ? T('w.confirming')
        : S.pay?.kind === 'deposit'
          ? T('w.pay_confirm', { amount: fmtMoney(S.pay.amountMinor, S.pay.currency) })
          : ownText('confirmLabel') || T('w.confirm'),
    )}</button></p>
  </form>
  ${contactFooter()}`;
}

function doneView(): string {
  const r = S.reservation;
  const cancelled = S.stage === 'cancelled';
  const status = T(`status.${r.status}`);
  return `
  ${brandHeader()}
  <h2 style="margin-top:8px">${esc(cancelled ? T('w.cancelled') : T('w.booked'))}</h2>
  ${S.error ? `<div class="notice">${esc(S.error)}</div>` : ''}
  ${!cancelled && ownText('thanks') ? `<p class="thanks">${esc(ownText('thanks'))}</p>` : ''}
  <div class="card">
    <p class="muted" style="margin:0">${esc(T('w.code'))}</p>
    <p class="code" style="margin:0 0 8px">${esc(r.code)}</p>
    <dl class="summary">
      <dt>${esc(T('w.row.restaurant'))}</dt><dd>${esc(S.rest!.name)}</dd>
      <dt>${esc(T('w.row.when'))}</dt><dd>${esc(whenLabel(r.startsAt, S.rest!.timezone))}</dd>
      <dt>${esc(T('w.row.party'))}</dt><dd>${esc(partyLabel(r.partySize))}</dd>
      <dt>${esc(T('w.row.name'))}</dt><dd>${esc(r.guest.name)}</dd>
      <dt>${esc(T('w.row.status'))}</dt><dd>${esc(status === `status.${r.status}` ? r.status : status)}</dd>
    </dl>
    ${cancelled ? '' : `<p style="margin:12px 0 0"><button type="button" data-act="cancel">${esc(T('w.cancel'))}</button></p>`}
  </div>
  <p style="margin-top:16px"><button class="link" type="button" data-act="new">${esc(T('w.new'))}</button></p>
  ${contactFooter()}`;
}

function render() {
  if (!S.rest) return;
  applyBranding();
  app.innerHTML = S.stage === 'details' && S.chosen ? detailsView() : S.stage === 'done' || S.stage === 'cancelled' ? doneView() : S.stage === 'waitlist' ? waitlistView() : searchView();
  notifyParent('size', { height: document.documentElement.scrollHeight });
  if (S.stage === 'details') void mountCard();
}

app.addEventListener('submit', (e) => {
  e.preventDefault();
  const form = e.target as HTMLFormElement;
  if (form.id === 'searchForm') {
    const fd = new FormData(form);
    S.date = String(fd.get('date'));
    S.time = String(fd.get('time'));
    S.party = Number(fd.get('party'));
    void search();
  } else if (form.id === 'detailsForm') {
    void confirm(form);
  } else if (form.id === 'waitForm') {
    void joinWaitlist(form);
  } else if (form.id === 'codeForm') {
    void applyCode(String(new FormData(form).get('code') ?? '').trim());
  } else if (form.id === 'eventForm') {
    void sendEvent(form);
  } else if (form.id === 'alertForm') {
    void sendAlert(form);
  }
});

/** When the date or time of an event request changes, show which rooms are already taken. */
app.addEventListener('change', (e) => {
  const form = (e.target as HTMLElement).closest('#eventForm') as HTMLFormElement | null;
  if (!form || !S.rest) return;
  const n = (k: string) => String((form.elements.namedItem(k) as HTMLInputElement | null)?.value ?? '');
  if (!n('date') || !n('from') || !n('to') || n('to') <= n('from')) return;
  void (async () => {
    try {
      const r = await api<{ roomIds: string[] }>('GET', `/v1/restaurants/${S.rest!.id}/private-rooms/free?date=${n('date')}&start=${n('from')}&end=${n('to')}`);
      const free = new Set(r.roomIds);
      const keep = n('room');
      takenRooms = new Set((S.rest!.privateRooms ?? []).filter((x) => !free.has(x.id)).map((x) => x.id));
      const saved = new Map([...new FormData(form).entries()].map(([k, v]) => [k, String(v)]));
      render();
      const after = document.getElementById('eventForm') as HTMLFormElement | null;
      document.querySelector('details.events')?.setAttribute('open', '');
      for (const [k, v] of saved) {
        const el = after?.elements.namedItem(k) as HTMLInputElement | null;
        if (el && el.type !== 'file') el.value = v;
      }
      const sel = after?.elements.namedItem('room') as HTMLSelectElement | null;
      if (sel && !takenRooms.has(keep)) sel.value = keep;
    } catch {
      /* the room list simply stays as it was */
    }
  })();
});

app.addEventListener('change', (e) => {
  const t = e.target as HTMLSelectElement;
  if (t.id === 'area' && S.chosen) {
    // Re-hold in the chosen area: release the first hold, then take a new one.
    const lockId = S.lock?.lockId;
    S.areaId = t.value;
    S.lock = null;
    void (async () => {
      if (lockId) await api('DELETE', `/v1/slot-locks/${lockId}`).catch(() => undefined);
      await takeHold();
    })();
  }
});

app.addEventListener('click', (e) => {
  const langBtn = (e.target as HTMLElement).closest('[data-lang]') as HTMLElement | null;
  if (langBtn) {
    const l = langBtn.dataset.lang;
    if (isLocale(l) && LOCALES.includes(l)) {
      // Keep what the guest typed while the page is redrawn in the new language.
      const form = document.getElementById('detailsForm') as HTMLFormElement | null;
      const saved = form ? [...new FormData(form).entries()] : [];
      setLocale(l);
      render();
      const after = document.getElementById('detailsForm') as HTMLFormElement | null;
      for (const [k, v] of saved) {
        const field = after?.elements.namedItem(k) as HTMLInputElement | null;
        if (field && field.type !== 'checkbox' && typeof v === 'string') field.value = v;
        else if (field && field.type === 'checkbox') field.checked = true;
      }
    }
    return;
  }
  const expBtn = (e.target as HTMLElement).closest('[data-exp]') as HTMLElement | null;
  if (expBtn) {
    S.expId = expBtn.dataset.exp ?? '';
    S.party = Math.min(Math.max(S.party, 1), S.rest!.onlineMaxParty);
    render();
    void search();
    return;
  }
  const el = (e.target as HTMLElement).closest('[data-slot],[data-day],[data-act],[data-alt]') as HTMLElement | null;
  if (!el) return;
  if (el.dataset.act === 'code-open') {
    codeOpen = true;
    render();
    return;
  }
  if (el.dataset.act === 'to-waitlist') {
    S.stage = 'waitlist';
    render();
    return;
  }
  if (el.dataset.slot !== undefined) {
    const s = S.days[S.activeDay].slots[Number(el.dataset.slot)];
    void choose(s);
  } else if (el.dataset.day !== undefined) {
    S.activeDay = Number(el.dataset.day);
    render();
  } else if (el.dataset.alt !== undefined) {
    const a = S.alternatives[Number(el.dataset.alt)];
    void choose({ startsAt: a.startsAt, localTime: a.localTime, offsetMinutes: 0, available: true, offerToken: a.offerToken });
  } else if (el.dataset.act === 'back') {
    void release();
  } else if (el.dataset.act === 'extend') {
    void extend();
  } else if (el.dataset.act === 'cancel') {
    void cancelBooking();
  } else if (el.dataset.act === 'new') {
    S.stage = 'search';
    S.reservation = null;
    S.error = '';
    S.idem = crypto.randomUUID();
    history.replaceState(null, '', location.pathname + (S.rest ? `?rid=${S.rest.id}` : ''));
    render();
    void search();
  }
});

if (previewMode) {
  window.addEventListener('message', (e) => {
    if (e.origin !== location.origin || !S.rest) return;
    const m = e.data as { type?: string; branding?: Restaurant['branding']; locale?: string; name?: string };
    if (m?.type !== 'preview-branding' || !m.branding) return;
    // The editor sends the draft's branding; the logo address is resolved the way the server does.
    S.rest.branding = {
      ...S.rest.branding,
      ...m.branding,
      widget: { ...DEFAULT_WIDGET_VIEW, ...(m.branding.widget ?? {}) } as WidgetSettings,
    };
    if (m.locale && isLocale(m.locale)) setLocale(m.locale, false);
    if (typeof m.name === 'string' && m.name.trim()) S.rest.name = m.name.trim();
    render();
  });
}

init().catch((e) => {
  app.innerHTML = `<p class="notice error">${esc(errorText(e))}</p>`;
});

// Unused in some builds; kept for the language names the switcher may show in full.
void LOCALE_NAMES;
